<script lang="ts">
  import { ChevronLeft, ChevronRight } from 'lucide-svelte';
  import { type DateValue, getLocalTimeZone, today } from '@internationalized/date';
  import { RangeCalendar as RC } from 'bits-ui';

  // Wrapper del RangeCalendar de bits-ui con estilos luxury warm-dark + acentos
  // primary. Look-feel iOS calendar (cards compactas, hover suave, range
  // continuo, días "out of view" atenuados, today con ring).
  //
  // API mínima: bind:value={ start?: DateValue; end?: DateValue }
  // Emite cambios via $bindable.

  interface Range {
    start: DateValue | undefined;
    end: DateValue | undefined;
  }

  type Props = {
    value?: Range;
    placeholder?: DateValue;
    minValue?: DateValue;
    maxValue?: DateValue;
    numberOfMonths?: number;
  };

  let {
    value = $bindable({ start: undefined, end: undefined }),
    placeholder = today(getLocalTimeZone()),
    minValue,
    maxValue,
    numberOfMonths = 1,
  }: Props = $props();
</script>

<RC.Root
  bind:value
  {placeholder}
  {minValue}
  {maxValue}
  {numberOfMonths}
  weekdayFormat="short"
  fixedWeeks={true}
  class="bg-popover text-foreground rounded-md p-3"
>
  {#snippet children({ months, weekdays })}
    <RC.Header class="flex items-center justify-between pb-3">
      <RC.PrevButton
        class="hover:bg-muted/60 text-muted-foreground hover:text-foreground inline-flex size-7 items-center justify-center rounded-md transition-colors"
      >
        <ChevronLeft class="size-4" />
      </RC.PrevButton>
      <RC.Heading class="text-foreground text-sm font-semibold tracking-tight" />
      <RC.NextButton
        class="hover:bg-muted/60 text-muted-foreground hover:text-foreground inline-flex size-7 items-center justify-center rounded-md transition-colors"
      >
        <ChevronRight class="size-4" />
      </RC.NextButton>
    </RC.Header>

    <div class="flex flex-col gap-4 sm:flex-row">
      {#each months as month (month.value)}
        <RC.Grid class="w-full border-collapse select-none space-y-1">
          <RC.GridHead>
            <RC.GridRow class="flex w-full">
              {#each weekdays as day, i (i)}
                <RC.HeadCell
                  class="text-muted-foreground/70 w-9 flex-1 text-center text-[10px] font-medium uppercase tracking-wider"
                >
                  {day.slice(0, 2)}
                </RC.HeadCell>
              {/each}
            </RC.GridRow>
          </RC.GridHead>
          <RC.GridBody>
            {#each month.weeks as weekDates, weekIdx (weekIdx)}
              <RC.GridRow class="mt-1.5 flex w-full">
                {#each weekDates as date, dayIdx (dayIdx)}
                  <RC.Cell
                    {date}
                    month={month.value}
                    class="relative size-9 flex-1 p-0 text-center text-sm
                      [&:has([data-selection-end])]:rounded-r-md
                      [&:has([data-selection-start])]:rounded-l-md
                      [&:has([data-selected])]:bg-primary/15
                      first:[&:has([data-selected])]:rounded-l-md
                      last:[&:has([data-selected])]:rounded-r-md"
                  >
                    <RC.Day
                      class="hover:bg-muted/60 focus-visible:ring-primary/40 inline-flex size-9 items-center justify-center rounded-md font-mono text-xs tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2
                        data-[outside-month]:text-muted-foreground/30
                        data-[outside-month]:pointer-events-none
                        data-[disabled]:text-muted-foreground/40
                        data-[disabled]:pointer-events-none
                        data-[unavailable]:text-muted-foreground/30
                        data-[unavailable]:line-through
                        data-[selected]:bg-primary/20
                        data-[selected]:text-foreground
                        data-[selection-start]:bg-primary
                        data-[selection-start]:text-primary-foreground
                        data-[selection-start]:hover:bg-primary
                        data-[selection-end]:bg-primary
                        data-[selection-end]:text-primary-foreground
                        data-[selection-end]:hover:bg-primary
                        data-[today]:ring-primary/60
                        data-[today]:ring-1"
                    />
                  </RC.Cell>
                {/each}
              </RC.GridRow>
            {/each}
          </RC.GridBody>
        </RC.Grid>
      {/each}
    </div>
  {/snippet}
</RC.Root>
